/** UI-side vocabulary for the survey section: filters, labels, badges, formatters. */

import type {
  SurveyPlacement,
  SurveyQuestionType,
  SurveyStatus,
} from "../../lib/api";

/* -------------------------------------------------------------------------- */
/* Status                                                                      */
/* -------------------------------------------------------------------------- */

export type SurveyStatusFilter =
  | "all"
  | "DRAFT"
  | "SCHEDULED"
  | "ACTIVE"
  | "CLOSED";

export const SURVEY_STATUS_TABS: { id: SurveyStatusFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "ACTIVE", label: "Active" },
  { id: "DRAFT", label: "Draft" },
  { id: "SCHEDULED", label: "Scheduled" },
  { id: "CLOSED", label: "Closed" },
];

/** Maps the filter pills → the `status` query param ("" = every status). */
export function statusFilterToApi(filter: SurveyStatusFilter): string {
  return filter === "all" ? "" : filter;
}

export function surveyStatusLabel(status: SurveyStatus): string {
  switch (String(status).toUpperCase()) {
    case "DRAFT":
      return "Draft";
    case "SCHEDULED":
      return "Scheduled";
    case "ACTIVE":
      return "Active";
    case "CLOSED":
      return "Closed";
    default:
      return String(status);
  }
}

export function surveyStatusBadgeClass(status: SurveyStatus): string {
  switch (String(status).toUpperCase()) {
    case "ACTIVE":
      return "bg-emerald-500/15 text-emerald-400 border-emerald-500/35";
    case "SCHEDULED":
      return "bg-sky-500/15 text-sky-300 border-sky-500/35";
    case "DRAFT":
      return "bg-amber-500/15 text-amber-300 border-amber-500/35";
    case "CLOSED":
      return "bg-[#2C363E] text-[#AEBCC6] border-[#3A4852]";
    default:
      return "bg-[#2C363E] text-[#AEBCC6] border-[#3A4852]";
  }
}

/* -------------------------------------------------------------------------- */
/* Question types                                                              */
/* -------------------------------------------------------------------------- */

/**
 * The v1 set. Kept as one list so the builder's type picker, the table's summary
 * line and the results renderer can never drift apart.
 */
export const QUESTION_TYPES: {
  type: SurveyQuestionType;
  label: string;
  hint: string;
}[] = [
  {
    type: "SINGLE_CHOICE",
    label: "Multiple choice",
    hint: "Pick one option",
  },
  {
    type: "MULTI_CHOICE",
    label: "Checkboxes",
    hint: "Pick as many as you like",
  },
  {
    type: "SHORT_TEXT",
    label: "Short answer",
    hint: "One line of text",
  },
  {
    type: "LONG_TEXT",
    label: "Paragraph",
    hint: "A few sentences, with optional suggested answers",
  },
  { type: "NPS", label: "NPS (0–10)", hint: "How likely to recommend" },
  { type: "RATING", label: "Rating (1–5)", hint: "Star rating" },
];

export function questionTypeLabel(type: SurveyQuestionType): string {
  return QUESTION_TYPES.find((t) => t.type === type)?.label ?? String(type);
}

export const isChoiceType = (type: SurveyQuestionType): boolean =>
  type === "SINGLE_CHOICE" || type === "MULTI_CHOICE";

export const isTextType = (type: SurveyQuestionType): boolean =>
  type === "SHORT_TEXT" || type === "LONG_TEXT";

export const isScaleType = (type: SurveyQuestionType): boolean =>
  type === "NPS" || type === "RATING";

/* -------------------------------------------------------------------------- */
/* Placements                                                                  */
/* -------------------------------------------------------------------------- */

export const PLACEMENT_OPTIONS: {
  value: SurveyPlacement;
  label: string;
  hint: string;
}[] = [
  {
    value: "DASHBOARD_CTA",
    label: "Dashboard",
    hint: "Banner on the home screen",
  },
  {
    value: "SETTINGS",
    label: "Settings",
    hint: "Row in the settings menu",
  },
  {
    value: "TRANSACTION_SUCCESS",
    label: "After a transaction",
    hint: "Prompt on the success screen",
  },
];

export function placementLabel(placement: SurveyPlacement): string {
  return (
    PLACEMENT_OPTIONS.find((p) => p.value === placement)?.label ??
    String(placement).replace(/_/g, " ").toLowerCase()
  );
}

/* -------------------------------------------------------------------------- */
/* Formatters                                                                  */
/* -------------------------------------------------------------------------- */

/** "Mar 3rd, 2026" — matches the date style used elsewhere in the admin tables. */
export function formatOrdinalDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const day = d.getDate();
  const suffix =
    day % 10 === 1 && day !== 11
      ? "st"
      : day % 10 === 2 && day !== 12
      ? "nd"
      : day % 10 === 3 && day !== 13
      ? "rd"
      : "th";
  const month = d.toLocaleString("en-US", { month: "short" });
  return `${month} ${day}${suffix}, ${d.getFullYear()}`;
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || Number.isNaN(Number(seconds))) return "—";
  const s = Math.round(Number(seconds));
  if (s < 60) return `${s}s`;
  const mins = Math.floor(s / 60);
  const rem = s % 60;
  return rem === 0 ? `${mins}m` : `${mins}m ${rem}s`;
}

export function formatPercent(value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return `${Number(value).toFixed(1).replace(/\.0$/, "")}%`;
}

export function formatCount(value: number | null | undefined): string {
  return Number(value ?? 0).toLocaleString();
}

/** Pulls a readable message out of an axios/Error failure for the retry banner. */
export function surveyErrorMessage(err: unknown, fallback: string): string {
  if (
    err &&
    typeof err === "object" &&
    "response" in err &&
    err.response &&
    typeof err.response === "object" &&
    "data" in err.response &&
    err.response.data &&
    typeof err.response.data === "object" &&
    "message" in err.response.data
  ) {
    const m = (err.response.data as { message?: unknown }).message;
    if (typeof m === "string") return m;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

/** Respondent's display name, falling back to email then "Anonymous". */
export function respondentName(
  user: { firstName?: string | null; lastName?: string | null; email?: string | null } | null
): string {
  if (!user) return "Anonymous";
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  return name || user.email || "Anonymous";
}
